import { useTranslation } from 'react-i18next';
import { Button } from './button';
import { Dialog } from './dialog';

/**
 * A yes/no confirmation on top of the shared modal shell (M1-30, first needed
 * for archiving a customer; M1-05's property archive is the second planned
 * caller).
 *
 * A native `window.confirm()` would have been the smaller diff, but this app
 * built one modal shell specifically so every dialog gets the same focus
 * trap, Escape handling and visual language — a browser-native confirm would
 * look and behave like nothing else here, and would need mocking
 * `window.confirm` in every test that triggers it instead of the same
 * `findByTestId`/`findByRole` pattern every other dialog already uses.
 *
 * Destructive by default (the two callers so far are both archive actions),
 * but not hardcoded to it — a future confirmation that isn't destructive
 * (e.g. "send this now?") shouldn't have to fight a red button.
 */
export function ConfirmDialog({
  titleId,
  title,
  description,
  confirmLabel,
  destructive = true,
  isPending = false,
  errorMessage,
  onConfirm,
  onClose,
  testId,
}: {
  titleId: string;
  title: string;
  description?: string;
  confirmLabel: string;
  destructive?: boolean;
  isPending?: boolean;
  /** Shown as an alert when the confirmed action has failed. */
  errorMessage?: string;
  onConfirm: () => void;
  onClose: () => void;
  testId?: string;
}) {
  const { t } = useTranslation();

  return (
    <Dialog
      titleId={titleId}
      title={title}
      description={description}
      onClose={onClose}
      testId={testId}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'default'}
            size="sm"
            disabled={isPending}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {/* A confirmation otherwise has no fields of its own. Dialog's
          open-focus effect searches this whole region including the footer,
          so with nothing focusable here the FIRST button in DOM order —
          Cancel, not the destructive action — gets focus, which is also the
          safer default. Text alone (the error message) does not change that. */}
      {errorMessage ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {errorMessage}
        </p>
      ) : null}
    </Dialog>
  );
}
